import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AudioContext = createContext(null);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const audioRef = useRef(null);
  
  const [currentScanner, setCurrentScanner] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  
  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);
  
  // Update volume when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);
  
  // Play a scanner feed
  const playScanner = useCallback((scanner) => {
    if (!token) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to listen to scanner feeds.',
        variant: 'destructive'
      });
      return;
    }
    
    if (!scanner || !scanner.id) {
      toast({
        title: 'Invalid Scanner',
        description: 'Unable to play this scanner feed.',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    
    // Stop current if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    
    // Build audio URL with token
    const audioUrl = `${API_URL}/api/streams/${scanner.id}/proxy?token=${token}`;
    console.log('Playing scanner:', scanner.name, audioUrl);
    
    audioRef.current.src = audioUrl;
    
    // Set up event handlers
    audioRef.current.oncanplay = () => {
      setIsLoading(false);
      setIsPlaying(true);
      setCurrentScanner(scanner);
      
      toast({
        title: 'Now Playing',
        description: `${scanner.name} - ${scanner.city}, ${scanner.state}`
      });
    };
    
    audioRef.current.onerror = (e) => {
      console.error('Audio stream error:', e);
      setIsLoading(false);
      setIsPlaying(false);
      setCurrentScanner(null);
      
      toast({
        title: 'Stream Unavailable',
        description: `${scanner.name} is currently offline. Try another feed.`,
        variant: 'destructive'
      });
    };
    
    audioRef.current.onended = () => {
      setIsPlaying(false);
    };
    
    // Start loading and playing
    audioRef.current.load();
    audioRef.current.play().catch(err => {
      console.error('Audio play failed:', err);
      setIsLoading(false);
      setIsPlaying(false);
      setCurrentScanner(null);
      
      toast({
        title: 'Playback Error',
        description: 'Unable to start audio playback. Please try again.',
        variant: 'destructive'
      });
    });
  }, [token, toast]);
  
  // Stop playback
  const stopScanner = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setIsPlaying(false);
    setCurrentScanner(null);
  }, []);
  
  // Toggle play/pause for current scanner
  const togglePlayPause = useCallback(() => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else if (currentScanner) {
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  }, [isPlaying, currentScanner]);
  
  // Mute/unmute
  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
    }
  }, []);
  
  const value = {
    currentScanner,
    isPlaying,
    isLoading,
    volume,
    setVolume,
    playScanner,
    stopScanner,
    togglePlayPause,
    toggleMute
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

export default AudioContext;
